/**
 * MSAL configuration for Azure AD B2C.
 * Maps existing authConfig (from config.ts / window) to the shape required by @azure/msal-browser.
 * See docs/AUTH_UPGRADE_README.md Part A.1.
 */

import type { Configuration } from '@azure/msal-browser';
import { PublicClientApplication } from '@azure/msal-browser';
import { authConfig } from 'src/config';

/** Shape of auth config we use (values from window/config). */
interface AuthConfigShape {
  instance: string;
  tenant: string;
  signInPolicy: string;
  clientId: string;
  redirectUri: string;
  scopes: unknown;
}
const config = authConfig as AuthConfigShape;

/**
 * Build B2C authority from instance, tenant and sign-in policy.
 * Format: {instance}{tenant}/{signInPolicy}
 * e.g. https://emsbkptyltdb2cdev.b2clogin.com/tfp/emsbkptyltdb2cdev.onmicrosoft.com/B2C_1A_SIGNUP_SIGNIN
 */
const getAuthority = (): string => {
  const { instance, tenant, signInPolicy } = config;
  const base = instance.endsWith('/') ? instance.slice(0, -1) : instance;
  return `${base}/${tenant}/${signInPolicy}`;
};

/**
 * Derive knownAuthorities for B2C (required for token validation).
 * Uses the host from the instance URL, e.g. emsbkptyltdb2cdev.b2clogin.com
 */
const getKnownAuthorities = (): string[] => {
  try {
    const url = new URL(config.instance);
    return [url.hostname];
  } catch {
    return [];
  }
};

/**
 * Scopes from AUTH_SCOPES (space-separated string) as array.
 */
const getScopes = (): string[] => {
  const scopes = config.scopes;
  if (typeof scopes !== 'string') {
    return [];
  }
  return scopes.split(/\s+/).filter(Boolean);
};

/**
 * MSAL browser configuration for Azure AD B2C.
 * Uses existing window/config.js AUTH_* values via authConfig.
 * Cast to Configuration for compatibility across MSAL minor versions (some options may be optional or renamed).
 */
export const msalConfig = {
  auth: {
    clientId: config.clientId,
    authority: getAuthority(),
    knownAuthorities: getKnownAuthorities(),
    redirectUri: config.redirectUri,
    postLogoutRedirectUri: `${config.redirectUri}/logout`,
  },
  cache: {
    cacheLocation: 'sessionStorage' as const,
  },
} as Configuration;

/**
 * Default scopes for login and token acquisition (from AUTH_SCOPES).
 */
export const loginRequestScopes: string[] = getScopes();

/**
 * Single PublicClientApplication instance for the app (create once, use in MsalProvider).
 */
let msalInstance: PublicClientApplication | null = null;

export const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};
